import { useQuery } from '@apollo/client';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useSelector } from 'react-redux';
import {
  BOOKING_SLOTS_BY_STUDIO,
  GET_STUDIO_BOOKING_AVAILABILITY,
  GET_STUDIO_OWNER_BOOKINGS,
} from '../graphql/queries/bookingQueries';

const BOOKING_SLOT_CONNECTION = 'bookingSlotsByStudioIDAndStartDateTime';
const BOOKING_SLOT_PAGE_LIMIT = 500;
const BUSY_BOOKING_SLOT_FILTER = {
  or: [
    { slotStatus: { eq: 'RESERVING' } },
    { slotStatus: { eq: 'HELD' } },
    { slotStatus: { eq: 'CONFIRMED' } },
  ],
};

const getSlotWindowStart = () => {
  const start = new Date();
  start.setHours(0, 0, 0, 0);
  start.setDate(start.getDate() - 1);
  return start.toISOString();
};

export const useFetchStudioBooking = (
  id,
  { includeBookings = false, includeAvailabilitySlots = true } = {}
) => {
  const authUser = useSelector((state) => state?.auth?.user || {});
  const isAuthenticated = Boolean(authUser?.id || authUser?._id || authUser?.sub);
  const slotWindowStart = useMemo(() => getSlotWindowStart(), [id]);
  const [slotPaginationLoading, setSlotPaginationLoading] = useState(false);
  const [slotPaginationError, setSlotPaginationError] = useState(null);
  const paginationStartTokenRef = useRef(null);

  const {
    data,
    loading,
    error,
  } = useQuery(GET_STUDIO_BOOKING_AVAILABILITY, {
    variables: { id },
    skip: !id,
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: slotData,
    loading: slotLoading,
    error: slotError,
    fetchMore: fetchMoreSlots,
    refetch: refetchSlots,
  } = useQuery(BOOKING_SLOTS_BY_STUDIO, {
    variables: {
      studioID: id,
      startDateTime: { ge: slotWindowStart },
      sortDirection: 'ASC',
      filter: BUSY_BOOKING_SLOT_FILTER,
      limit: BOOKING_SLOT_PAGE_LIMIT,
    },
    skip: !id || !isAuthenticated || !includeAvailabilitySlots,
    fetchPolicy: 'cache-and-network',
    notifyOnNetworkStatusChange: true,
  });

  const {
    data: ownerBookingData,
    loading: ownerBookingLoading,
    error: ownerBookingError,
  } = useQuery(GET_STUDIO_OWNER_BOOKINGS, {
    variables: { id },
    skip: !id || !includeBookings,
    fetchPolicy: 'cache-and-network',
  });

  useEffect(() => {
    paginationStartTokenRef.current = null;
    setSlotPaginationError(null);
  }, [id, slotWindowStart]);

  useEffect(() => {
    const firstNextToken = slotData?.[BOOKING_SLOT_CONNECTION]?.nextToken;
    if (
      !id ||
      !isAuthenticated ||
      !includeAvailabilitySlots ||
      !firstNextToken ||
      !fetchMoreSlots
    ) return;
    if (paginationStartTokenRef.current === firstNextToken) return;

    let cancelled = false;
    paginationStartTokenRef.current = firstNextToken;
    setSlotPaginationLoading(true);
    setSlotPaginationError(null);

    const loadRemainingSlotPages = async () => {
      let nextToken = firstNextToken;

      try {
        while (nextToken && !cancelled) {
          const result = await fetchMoreSlots({
            variables: { nextToken },
            updateQuery: (previousResult, { fetchMoreResult }) => {
              const previousConnection = previousResult?.[BOOKING_SLOT_CONNECTION];
              const nextConnection = fetchMoreResult?.[BOOKING_SLOT_CONNECTION];
              if (!previousConnection || !nextConnection) {
                return fetchMoreResult || previousResult;
              }

              return {
                ...previousResult,
                [BOOKING_SLOT_CONNECTION]: {
                  ...nextConnection,
                  items: [
                    ...(previousConnection.items || []),
                    ...(nextConnection.items || []),
                  ],
                },
              };
            },
          });

          nextToken = result?.data?.[BOOKING_SLOT_CONNECTION]?.nextToken || null;
        }
      } catch (err) {
        if (!cancelled) {
          setSlotPaginationError(err);
        }
      } finally {
        if (!cancelled) {
          setSlotPaginationLoading(false);
        }
      }
    };

    loadRemainingSlotPages();

    return () => {
      cancelled = true;
    };
  }, [fetchMoreSlots, id, includeAvailabilitySlots, isAuthenticated, slotData]);

  const availabilityError = slotError || slotPaginationError || null;

  const bookingStudioData = useMemo(() => {
    const studio = data?.getStudio;
    if (!studio) return null;

    const slotItems = slotData?.[BOOKING_SLOT_CONNECTION]?.items;
    const ownerBookings = ownerBookingData?.getStudio?.bookings;
    const busyIntervalsUnavailable =
      includeAvailabilitySlots && (!isAuthenticated || Boolean(availabilityError));

    return {
      ...studio,
      ...(Array.isArray(slotItems) ? { bookingSlots: { items: slotItems } } : {}),
      ...(busyIntervalsUnavailable ? { busyIntervalsUnavailable: true } : {}),
      ...(includeBookings && ownerBookings ? { bookings: ownerBookings } : {}),
    };
  }, [
    availabilityError,
    data?.getStudio,
    includeAvailabilitySlots,
    includeBookings,
    isAuthenticated,
    ownerBookingData?.getStudio?.bookings,
    slotData,
  ]);

  return {
    bookingStudioData,
    loading: loading || slotLoading || slotPaginationLoading || ownerBookingLoading,
    error: error || ownerBookingError || null,
    availabilityError,
    refetchAvailability: refetchSlots,
  };
};
